import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCachedResource } from "../hooks/useCachedResource";
import { createAsset, listAssets, runDepreciation } from "../api/accounting";
import { AcctShell } from "../features/acct/AcctShell";
import { AssetRow } from "../features/acct/AssetRow";
import { NeuButton, NeuCard, NeuInput } from "../components/neu/Neu";

export default function AcctAssetsPage() {
  const { t } = useTranslation();
  const { data, reload: load } = useCachedResource("acct-assets", () => listAssets({ status: "all" }).catch(() => []));
  const rows = data || [];
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [form, setForm] = useState({ name: "", cost: "", useful_life_months: 60, residual_value: "" });

  const save = async () => {
    await createAsset({
      name: form.name,
      purchase_date: new Date().toISOString().slice(0, 10),
      cost: Number(form.cost),
      residual_value: Number(form.residual_value || 0),
      useful_life_months: Number(form.useful_life_months),
    });
    toast.success(t("acct.assetCreated"));
    setForm({ name: "", cost: "", useful_life_months: 60, residual_value: "" });
    load();
  };

  const depreciate = async () => {
    const res = await runDepreciation(month);
    toast.success(res.posted ? `${t("acct.depreciationPosted")} ${res.entry_number}` : t("acct.nothingToDepreciate"));
    load();
  };

  return (
    <AcctShell titleKey="assets">
      <NeuCard className="grid gap-3 p-5 sm:grid-cols-5" data-testid="asset-form">
        <NeuInput data-testid="asset-name" placeholder={t("acct.assetName")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <NeuInput data-testid="asset-cost" type="number" placeholder={t("acct.cost")} value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
        <NeuInput data-testid="asset-residual" type="number" placeholder={t("acct.residual")} value={form.residual_value} onChange={(e) => setForm({ ...form, residual_value: e.target.value })} />
        <NeuInput data-testid="asset-life" type="number" placeholder={t("acct.lifeMonths")} value={form.useful_life_months} onChange={(e) => setForm({ ...form, useful_life_months: e.target.value })} />
        <NeuButton variant="primary" data-testid="asset-save" disabled={!form.name || !form.cost} onClick={save}>
          {t("app.save")}
        </NeuButton>
      </NeuCard>

      <NeuCard className="flex flex-wrap items-end gap-3 p-5" data-testid="depreciation-box">
        <div className="w-40">
          <p className="label-xs mb-2">{t("acct.depreciationMonth")}</p>
          <NeuInput data-testid="depreciation-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </div>
        <NeuButton data-testid="depreciation-run" onClick={depreciate}>
          {t("acct.runDepreciation")}
        </NeuButton>
      </NeuCard>

      <div className="grid gap-2" data-testid="asset-list">
        {rows.map((row) => (
          <AssetRow key={row.id} row={row} onDone={load} />
        ))}
      </div>
    </AcctShell>
  );
}
